import { Box, Input, Button, Flex, Text } from '@chakra-ui/react';
import { FieldLabel } from '../Field';
import {
  ButtonIcon,
  FileUpload,
  FormFeedback,
  Switch
} from '@morressier/alembic';
import { IExportFormValues } from '@publishing-engine/domain/models/vendor-exports';
import {
  FieldErrors,
  UseFormGetValues,
  UseFormRegister,
  UseFormSetValue
} from 'react-hook-form';
import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleCheck,
  faCircleXmark,
  faTrashAlt
} from '@fortawesome/pro-regular-svg-icons';
import { getServiceAccountError } from './utils/VendorExportUtilities';

interface VendorExportServerInfoProps {
  type: string;
  isSubmitting: boolean;
  errors: FieldErrors<IExportFormValues>;
  testConnection: (values: IExportFormValues) => Promise<boolean | string>;
  getValues: UseFormGetValues<IExportFormValues>;
  register: UseFormRegister<IExportFormValues>;
  setValue: UseFormSetValue<IExportFormValues>;
}
const VendorExportServerInfo = ({
  type,
  isSubmitting,
  errors,
  testConnection,
  getValues,
  register,
  setValue
}: VendorExportServerInfoProps) => {
  const [connectionStatus, setConnectionStatus] = useState<string | null>(null);

  const handleTestConnection = async () => {
    const result = await testConnection(getValues());
    if (typeof result === 'boolean') {
      setConnectionStatus(result ? 'success' : 'failed');
    } else {
      setConnectionStatus('failed');
    }
  };

  const renderConnectionTest = (status: string | null) => {
    switch (status) {
      case 'success':
        return (
          <Flex alignItems={'center'} gap={2}>
            <FontAwesomeIcon icon={faCircleCheck}></FontAwesomeIcon>
            <Text color="green" fontWeight={'semibold'}>
              Connection successful
            </Text>
          </Flex>
        );
      case 'failed':
        return (
          <Flex alignItems={'center'} gap={2}>
            <FontAwesomeIcon icon={faCircleXmark}></FontAwesomeIcon>
            <Text color="red" fontWeight={'semibold'}>
              Connection failed
            </Text>
          </Flex>
        );
      default:
        return null;
    }
  };
  const [serviceAccountFileName, setServiceAccountFileName] = useState<
    string | null
  >(null);

  const handleServiceAccountUpload = async (files: File[]) => {
    const file = files[0];
    if (!file) return;

    setServiceAccountFileName(file.name);

    try {
      const json = JSON.parse(await file.text());

      setValue('serverInfo.private_key', json.private_key, {
        shouldDirty: true,
        shouldValidate: true
      });

      setValue('serverInfo.client_email', json.client_email, {
        shouldDirty: true,
        shouldValidate: true
      });

      setValue('serverInfo.project_id', json.project_id, {
        shouldDirty: true,
        shouldValidate: true
      });
    } catch {
      setServiceAccountFileName(null);
    }
  };
  const removeServiceAccountFile = () => {
    setServiceAccountFileName(null);
    setValue('serverInfo.private_key', undefined);
    setValue('serverInfo.client_email', undefined);
    setValue('serverInfo.project_id', undefined);
  };
  return (
    <Box bg={'#f9fafb'} p={{ base: 3, md: 5 }} mt={2} mb={4}>
      {(type === 'sftp' || type === 'ftp') && (
        <Flex direction={'column'} gap={4}>
          <Flex justifyContent="space-between" gap={4} flexDirection={{ base: 'column', md: 'row' }}>
            <Flex direction="column" width="100%">
              <FieldLabel subText="" label="Host" />
              <Input
                {...register('serverInfo.host')}
                bg="white"
                placeholder="Enter host"
              />
              {errors.serverInfo?.host && (
                <FormFeedback variant="error">
                  {errors.serverInfo.host.message}
                </FormFeedback>
              )}
            </Flex>
            <Flex direction="column" width="100%">
              <FieldLabel subText="" label="Port" />
              <Input
                {...register('serverInfo.port')}
                bg="white"
                placeholder="Enter port"
              />
              {errors.serverInfo?.port && (
                <FormFeedback variant="error">
                  {errors.serverInfo.port.message}
                </FormFeedback>
              )}
            </Flex>
          </Flex>

          <Flex justifyContent="space-between" gap={4} flexDirection={{ base: 'column', md: 'row' }}>
            <Flex direction="column" width="100%">
              <FieldLabel subText="" label="Username" />
              <Input
                {...register('serverInfo.username')}
                bg="white"
                placeholder="Enter username"
              />
              {errors.serverInfo?.username && (
                <FormFeedback variant="error">
                  {errors.serverInfo.username.message}
                </FormFeedback>
              )}
            </Flex>
            <Flex direction="column" width="100%">
              <FieldLabel subText="" label="Password" />
              <Input
                type="password"
                {...register('serverInfo.password')}
                bg="white"
                autoComplete="off"
                placeholder="Enter password"
              />
              {errors.serverInfo?.password && (
                <FormFeedback variant="error">
                  {errors.serverInfo.password.message}
                </FormFeedback>
              )}
            </Flex>
          </Flex>

          <Flex direction={'column'} width={{ base: '100%', md: '50%' }}>
            <FieldLabel subText="" label="Remote Path" />
            <Input
              {...register('serverInfo.path')}
              bg="white"
              placeholder="Enter remote path"
            />
            {errors.serverInfo?.path && (
              <FormFeedback variant="error">
                {errors.serverInfo.path.message}
              </FormFeedback>
            )}
          </Flex>
        </Flex>
      )}
      {type === 's3' && (
        <Flex direction={'column'} gap={4}>
          <Flex direction="column" width="100%">
            <FieldLabel subText="" label="Bucket Name" />
            <Input
              {...register('serverInfo.bucket')}
              bg="white"
              placeholder="Enter bucket name"
            />
            {errors.serverInfo?.bucket && (
              <FormFeedback variant="error">
                {errors.serverInfo.bucket.message}
              </FormFeedback>
            )}
          </Flex>

          <Flex direction="column" width="100%">
            <FieldLabel subText="" label="Region" />
            <Input
              {...register('serverInfo.region')}
              bg="white"
              placeholder="Enter region"
            />
            {errors.serverInfo?.region && (
              <FormFeedback variant="error">
                {errors.serverInfo.region.message}
              </FormFeedback>
            )}
          </Flex>

          <Flex justifyContent="space-between" gap={4} flexDirection={{ base: 'column', md: 'row' }}>
            <Flex direction="column" width="100%">
              <FieldLabel subText="" label="Access Key" />
              <Input
                {...register('serverInfo.access_key')}
                bg="white"
                placeholder="Enter access key"
              />
              {errors.serverInfo?.access_key && (
                <FormFeedback variant="error">
                  {errors.serverInfo.access_key.message}
                </FormFeedback>
              )}
            </Flex>
            <Flex direction="column" width="100%">
              <FieldLabel subText="" label="Secret Access Key" />
              <Input
                type="password"
                {...register('serverInfo.secret_key')}
                bg="white"
                autoComplete="off"
                placeholder="Enter secret key"
              />
              {errors.serverInfo?.secret_key && (
                <FormFeedback variant="error">
                  {errors.serverInfo.secret_key.message}
                </FormFeedback>
              )}
            </Flex>
          </Flex>

          <Flex direction="column" width={{ base: '100%', md: '50%' }}>
            <FieldLabel subText="" label="Path" />
            <Input
              {...register('serverInfo.path')}
              bg="white"
              placeholder="Enter path"
            />
            {errors.serverInfo?.path && (
              <FormFeedback variant="error">
                {errors.serverInfo.path.message}
              </FormFeedback>
            )}
          </Flex>
        </Flex>
      )}
      {type === 'gcs' && (
        <Flex direction="column" gap={4}>
          <Flex justifyContent="space-between" gap={2} flexDirection={{ base: 'column', md: 'row' }}>
            <Flex direction="column" width="100%">
              <FieldLabel label="Bucket Name" subText="" />
              <Input
                {...register('serverInfo.bucket')}
                bg="white"
                placeholder="Enter bucket name"
              />
              {errors.serverInfo?.bucket && (
                <FormFeedback variant="error">
                  {errors.serverInfo.bucket.message}
                </FormFeedback>
              )}
            </Flex>

            <Flex direction="column" width="100%">
              <FieldLabel label="Path" subText="" />
              <Input
                {...register('serverInfo.path')}
                bg="white"
                placeholder="Enter path"
              />
              {errors.serverInfo?.path && (
                <FormFeedback variant="error">
                  {errors.serverInfo.path.message}
                </FormFeedback>
              )}
            </Flex>
          </Flex>

          <Flex direction="column" w="100%">
            <FieldLabel
              label="Service account (JSON)"
              subText="Upload a Google Cloud service account key file with storage access"
            />

            <FileUpload
              uploadButtonText="Upload service account JSON"
              multiple={false}
              accept={{
                'application/json': ['.json']
              }}
              onDrop={handleServiceAccountUpload}
            />
            {(errors.serverInfo?.private_key ||
              errors.serverInfo?.client_email ||
              errors.serverInfo?.project_id) && (
              <FormFeedback variant="error">
                {getServiceAccountError(serviceAccountFileName, errors)}
              </FormFeedback>
            )}

            {serviceAccountFileName && (
              <Flex alignItems="center" gap={2} mt={2}>
                <FormFeedback variant="success">
                  {serviceAccountFileName}
                </FormFeedback>
                <ButtonIcon
                  size="small"
                  ariaLabel="Remove service account file"
                  icon={<FontAwesomeIcon icon={faTrashAlt} />}
                  onClick={removeServiceAccountFile}
                />
              </Flex>
            )}
          </Flex>
        </Flex>
      )}
      {type === 'api' && (
        <Flex direction={'column'} gap={4}>
          <FieldLabel label="API Endpoint" subText="" />
          <Input
            type="text"
            value={`/scipris-export/api/submission/:submissionId/scipris-export`}
            disabled
            {...register('serverInfo.endpoint')}
          />
          <Flex gap={4}>
            <FieldLabel label="Push sample data" subText="" />
            <Switch {...register('serverInfo.shouldPush')}/>
          </Flex>
        </Flex>
      )}
      <Flex justifyContent={'flex-start'} alignItems={'center'} gap={3} mt={3}>
        <Button
          onClick={handleTestConnection}
          isDisabled={isSubmitting}
          variant={'secondary'}
        >
          Test connection
        </Button>
        {renderConnectionTest(connectionStatus)}
      </Flex>
    </Box>
  );
};

export default VendorExportServerInfo;
